import { useState } from 'react';
import { useHistory } from 'react-router';
import { Checkbox, TextButton, Typography, DropdownMenu, useOverlayContext, ConfirmationModal, RadioButtonWithLabel } from 'seagull';
import { WideArrowDownward } from 'seagull/icons';
import { getVendorReminderDays, getClientReminderDays, getClientReminderTimes } from "../../business-logic/notificationSettings";
import { routes } from "../../constants";
import { useStore, useStoreActions } from "../../contexts";
import { handlePromise } from "../../infra/utils";
import { NotificationSettingsEntryType, NotificationSettingsModel } from "../../models/NotificationSettingsModel";
import { selectors } from "../../store/state-selectors";
import pluralize from "../../utils/pluralize";
import styles from "./EditNotificationSettingsModal.module.scss";
import { Tab, Table, isDays, minutesToDays, daysToMinutes, getRecipientTypesText, handleSetRecipientTypes, getFallbackRecipientTypes, getEditDropdownRecipientTypeText } from "./NotificationSettings.utils";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export const EditNotificationSettingsModal = _ref => {
  let {
    tableType,
    selectedTab,
    notification,
    notificationText,
    notificationName,
    refresh,
    dismissModal
  } = _ref;
  const {
    api
  } = useStoreActions();
  const history = useHistory();
  const businessId = useStore(selectors.user.businessId);
  const overlayContext = useOverlayContext();
  const notificationSettings = new NotificationSettingsModel(notification);
  const [times, setTimes] = useState(notificationSettings.recurringTimes());
  const [minutes, setMinutes] = useState(notificationSettings.recurringMinutes());
  const [recipientTypes, setRecipientTypes] = useState(notification.recipientTypes);
  const handleToggle = () => {
    setRecipientTypes(recipientTypes => getFallbackRecipientTypes({
      recipientTypes,
      defaultRecipientType: notification.recipientTypeOptions[0],
      originalRecipientTypes: notification.recipientTypes
    }));
  };
  return /*#__PURE__*/_jsx(ConfirmationModal, {
    title: "Edit notification settings",
    testId: "edit-notification-modal",
    content: /*#__PURE__*/_jsxs("div", {
      className: styles.body,
      children: [/*#__PURE__*/_jsx(Typography, {
        weight: "bold",
        testId: "edit-notification-modal-notification-text",
        children: notificationText
      }), /*#__PURE__*/_jsx(EditModalOptions, {
        times: times,
        minutes: minutes,
        editKey: "".concat(selectedTab, "-").concat(tableType),
        recipientTypes: recipientTypes,
        notificationName: notificationName,
        recipientTypeOptions: notification.recipientTypeOptions,
        setMinutes: setMinutes,
        setTimes: setTimes,
        setRecipientTypes: setRecipientTypes,
        handleToggle: handleToggle
      })]
    }),
    primaryButtonLabel: "Save",
    primaryButtonTestId: "edit-notification-modal-save",
    onPrimaryButtonClick: async () => {
      if (!businessId) {
        history.push(routes.home);
        return;
      }
      const data = notification.type === NotificationSettingsEntryType.Recurring ? notificationSettings.makeRecurringNotificationSettingsEntryUpdate(recipientTypes, {
        minutes: minutes || notification.recurring.minutes,
        times: times || notification.recurring.times
      }) : notificationSettings.makeSingleNotificationSettingsEntryUpdate(recipientTypes);
      overlayContext.startLoader('UpdateNotificationSettings');
      const result = await handlePromise(api.notification.updateNotificationSettings({
        data,
        businessId,
        notificationName
      }));
      if (result.success) {
        await refresh();
        overlayContext.showSuccessToast('Notification settings updated successfully', 'edit-notification-success');
      } else {
        overlayContext.showErrorToast('Failed to update notification settings', 'edit-notification-error');
      }
      overlayContext.stopLoader('UpdateNotificationSettings');
      dismissModal();
    },
    secondaryButtonLabel: "Cancel",
    secondaryButtonTestId: "edit-notification-modal-cancel",
    onSecondaryButtonClick: dismissModal,
    onCloseButtonClick: dismissModal
  });
};
const EditModalOptions = _ref2 => {
  let {
    times,
    minutes,
    editKey,
    recipientTypes,
    notificationName,
    recipientTypeOptions,
    setTimes,
    setMinutes,
    setRecipientTypes,
    handleToggle
  } = _ref2;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(RadioButtonWithLabel, {
      testId: "do-not-send-notification",
      label: /*#__PURE__*/_jsx(Typography, {
        className: styles.option,
        children: editDetailsMap[editKey].doNotSendText
      }),
      checked: !NotificationSettingsModel.hasRecipients({
        recipientTypes
      }),
      onChange: handleToggle
    }), /*#__PURE__*/_jsx(RadioButtonWithLabel, {
      testId: "notify-recipients",
      label: /*#__PURE__*/_jsxs(Typography, {
        className: styles.option,
        children: ["".concat(editDetailsMap[editKey].sendText, " "), editDetailsMap[editKey].render({
          times,
          minutes,
          recipientTypes,
          recipientTypeOptions,
          notificationName,
          setTimes,
          setMinutes,
          setRecipientTypes
        })]
      }),
      checked: NotificationSettingsModel.hasRecipients({
        recipientTypes
      }),
      onChange: handleToggle
    })]
  });
};
const DropdownButton = _ref3 => {
  let {
    testId,
    dropdownItems,
    children
  } = _ref3;
  return children && /*#__PURE__*/_jsx(DropdownMenu, {
    hAlign: "left",
    maxHeight: "50%",
    target: /*#__PURE__*/_jsx(TextButton, {
      weight: "bold",
      suffixIcon: /*#__PURE__*/_jsx(WideArrowDownward, {}),
      testId: testId,
      className: styles.textButton,
      children: children
    }),
    children: dropdownItems
  });
};
const editDetailsMap = {
  ["".concat(Tab.Vendor, "-").concat(Table.Notifications)]: {
    sendText: 'Notify',
    doNotSendText: 'Do not notify',
    render: _ref4 => {
      let {
        recipientTypes,
        notificationName,
        recipientTypeOptions,
        setRecipientTypes
      } = _ref4;
      return NotificationSettingsModel.hasRecipients({
        recipientTypes
      }) && /*#__PURE__*/_jsx(RecipientTypesDropdown, {
        recipientTypes: recipientTypes,
        notificationName: notificationName,
        recipientTypeOptions: recipientTypeOptions,
        setRecipientTypes: setRecipientTypes
      });
    }
  },
  ["".concat(Tab.Vendor, "-").concat(Table.Reminders)]: {
    sendText: 'Notify',
    doNotSendText: 'Do not notify',
    render: _ref5 => {
      let {
        minutes,
        recipientTypes,
        notificationName,
        recipientTypeOptions,
        setMinutes,
        setRecipientTypes
      } = _ref5;
      return !!minutes && NotificationSettingsModel.hasRecipients({
        recipientTypes
      }) && /*#__PURE__*/_jsxs("span", {
        children: [/*#__PURE__*/_jsx(RecipientTypesDropdown, {
          recipientTypes: recipientTypes,
          notificationName: notificationName,
          recipientTypeOptions: recipientTypeOptions,
          setRecipientTypes: setRecipientTypes
        }), "after ", /*#__PURE__*/_jsx(DaysDropdown, {
          options: getVendorReminderDays(),
          minutes: minutes,
          setMinutes: setMinutes
        })]
      });
    }
  },
  ["".concat(Tab.Client, "-").concat(Table.Notifications)]: {
    sendText: 'Notify my client',
    doNotSendText: 'Do not notify my client',
    render: () => /*#__PURE__*/_jsx(_Fragment, {})
  },
  ["".concat(Tab.Client, "-").concat(Table.Reminders)]: {
    sendText: 'Remind my client ',
    doNotSendText: 'Do not remind my client',
    render: _ref6 => {
      let {
        times,
        minutes,
        recipientTypes,
        setTimes,
        setMinutes
      } = _ref6;
      return !!minutes && !!times && NotificationSettingsModel.hasRecipients({
        recipientTypes
      }) && /*#__PURE__*/_jsxs("span", {
        children: ["every ", /*#__PURE__*/_jsx(DaysDropdown, {
          options: getClientReminderDays(),
          minutes: minutes,
          setMinutes: setMinutes
        }), "up to ", /*#__PURE__*/_jsx(DropdownButton, {
          dropdownItems: getClientReminderTimes().map(timesOption => /*#__PURE__*/_jsx(DropdownMenu.Item, {
            onClick: () => setTimes(timesOption),
            testId: "edit-times-dropdown-item-".concat(timesOption),
            children: /*#__PURE__*/_jsx(Typography, {
              children: pluralize('time', timesOption, true)
            })
          }, timesOption)),
          testId: "edit-times",
          children: pluralize('time', times, true)
        })]
      });
    }
  }
};
const RecipientTypesDropdown = _ref7 => {
  let {
    recipientTypes,
    notificationName,
    recipientTypeOptions,
    setRecipientTypes
  } = _ref7;
  return /*#__PURE__*/_jsx(DropdownButton, {
    dropdownItems: recipientTypeOptions.map(type => /*#__PURE__*/_jsx(DropdownMenu.Item, {
      as: Checkbox,
      closeOnClick: false,
      checked: recipientTypes.includes(type),
      onChange: e => handleSetRecipientTypes({
        newType: type,
        newRecipientTypes: e.currentTarget.checked ? [...recipientTypes, type] : recipientTypes.filter(x => x !== type),
        setRecipientTypes
      }),
      testId: "edit-recipient-types-dropdown-item-".concat(type),
      children: /*#__PURE__*/_jsx(Typography, {
        children: getEditDropdownRecipientTypeText(notificationName)[type]
      })
    }, type)),
    testId: "edit-recipient-types",
    children: getRecipientTypesText(recipientTypes, notificationName).join(', ')
  });
};
const DaysDropdown = _ref8 => {
  let {
    options,
    minutes,
    setMinutes
  } = _ref8;
  return /*#__PURE__*/_jsx(DropdownButton, {
    dropdownItems: options.map(daysOption => /*#__PURE__*/_jsx(DropdownMenu.Item, {
      onClick: () => setMinutes(daysToMinutes(daysOption)),
      testId: "edit-days-dropdown-item-".concat(daysOption),
      children: /*#__PURE__*/_jsx(Typography, {
        children: pluralize('day', daysOption, true)
      })
    }, daysOption)),
    testId: "edit-days",
    children: isDays(minutes) ? pluralize('day', minutesToDays(minutes), true) : pluralize('minute', minutes, true)
  });
};